import type { TFunction } from "i18next";
import { Loader2, Mic, MicOff, Square } from "lucide-react";
import type { JSX } from "react";
import { type ReactNode, useCallback, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import {
  DICTATION_MAX_SECONDS,
  type DictationError,
  type DictationStatus,
} from "@/hooks/transcriber/useDictation";
import {
  type FieldDictation,
  useFieldDictation,
} from "@/hooks/transcriber/useFieldDictation";
import { cn } from "@/lib/utils";
import { DictationMicMenu } from "./DictationMicMenu";

export const formatDictationSeconds = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

// Last stretch before the cap: say that it stops by itself.
const ENDING_SOON_SECONDS = 30;

/** The one status sentence for a dictation, shared by every mic. */
export const dictationMessage = (
  t: TFunction,
  {
    status,
    error,
    elapsedSeconds,
    listening = "dictation.listening",
  }: {
    status: DictationStatus;
    error: DictationError | null;
    elapsedSeconds: number;
    listening?: "dictation.listening" | "dictation.field.listening";
  },
): string | null => {
  const max = formatDictationSeconds(DICTATION_MAX_SECONDS);
  if (status === "listening") {
    return DICTATION_MAX_SECONDS - elapsedSeconds <= ENDING_SOON_SECONDS
      ? t("dictation.endingSoon", { max })
      : t(listening);
  }
  if (status !== "idle") return t(`dictation.${status}`);
  if (error === "permission") return t("dictation.permissionError");
  if (error === "offline") return t("dictation.offlineError");
  if (error === "connection") return t("dictation.connectionError");
  if (error) return t("dictation.error");
  return null;
};

type ButtonProps = {
  dictation: FieldDictation;
  // What is dictated into, in a sentence ("the note"): read by screen readers.
  fieldLabel: string;
  describedBy?: string;
  className?: string;
};

/**
 * The mic toggle. It never takes focus from the field on click (the cursor
 * stays where the words go). Visibly 32px; its hit area is 44px.
 */
export const DictationButton = ({
  dictation,
  fieldLabel,
  describedBy,
  className,
}: ButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const { status, error, isActive } = dictation;
  const label = t(isActive ? "dictation.field.stop" : "dictation.field.start", {
    field: fieldLabel,
  });
  return (
    <button
      ref={dictation.buttonRef}
      aria-describedby={describedBy}
      aria-label={label}
      aria-pressed={isActive}
      data-dictation-status={status}
      data-testid="dictation-button"
      disabled={status === "finishing"}
      title={label}
      type="button"
      className={cn(
        "relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
        "before:absolute before:-inset-1.5 before:content-['']",
        "text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:cursor-wait disabled:opacity-70",
        "[&_svg]:size-4",
        isActive &&
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:text-destructive-foreground",
        status === "listening" && "animate-pulse",
        className,
      )}
      onMouseDown={(event) => {
        // Keep the cursor in the field.
        event.preventDefault();
      }}
      onClick={dictation.toggle}
    >
      {(status === "connecting" || status === "finishing") && (
        <Loader2 className="animate-spin" />
      )}
      {status === "listening" && <Square className="fill-current" />}
      {status === "idle" &&
        (error ? <MicOff className="text-destructive" /> : <Mic />)}
    </button>
  );
};

/**
 * One quiet line under the field while dictating (live words in grey, the
 * timer against the 3-minute cap) or after it failed. Nothing otherwise.
 */
export const DictationStatusLine = ({
  dictation,
  id,
  className,
}: {
  dictation: FieldDictation;
  id?: string;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const { status, error, elapsedSeconds, interim } = dictation;
  const message = dictationMessage(t, {
    status,
    error,
    elapsedSeconds,
    listening: "dictation.field.listening",
  });
  return (
    <p
      aria-live="polite"
      id={id}
      className={cn(
        "flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground",
        !message && "sr-only",
        error && status === "idle" && "text-destructive",
        className,
      )}
      data-testid="dictation-status"
    >
      {status === "listening" && (
        <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-destructive" />
      )}
      <span className="min-w-0 shrink-0 max-sm:shrink">{message}</span>
      {interim && (
        <span
          aria-hidden
          className="min-w-0 truncate italic opacity-70"
          data-testid="dictation-interim"
        >
          {interim}
        </span>
      )}
      {status === "listening" && (
        <span
          aria-hidden
          className="ml-auto shrink-0 pl-2 tabular-nums"
          data-testid="dictation-elapsed"
        >
          {formatDictationSeconds(elapsedSeconds)} /{" "}
          {formatDictationSeconds(DICTATION_MAX_SECONDS)}
        </span>
      )}
    </p>
  );
};

type WithDictationProps = {
  children: ReactNode;
  fieldLabel: string;
  // Textareas: the mic sits bottom-right; inputs: centred on the right.
  multiline?: boolean;
  language?: string;
  className?: string;
};

const WithDictationEnabled = ({
  children,
  fieldLabel,
  multiline = false,
  language,
  className,
}: WithDictationProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const statusId = useId();
  const getTarget = useCallback(
    () =>
      containerRef.current?.querySelector<HTMLElement>(
        "textarea, input, [contenteditable='true']",
      ) ?? null,
    [],
  );
  const dictation = useFieldDictation({ getTarget, language });
  return (
    <div className={cn("space-y-1", className)}>
      <div
        ref={containerRef}
        className={cn(
          "relative rounded-md transition-shadow [&_input]:pr-11",
          // Textareas also carry the microphone chevron next to the mic.
          multiline ? "[&_textarea]:pr-16" : "[&_textarea]:pr-11",
          dictation.status === "listening" && "ring-2 ring-destructive/40",
        )}
        data-dictation-field
      >
        {children}
        <DictationButton
          className={cn(
            "absolute right-1.5",
            multiline ? "bottom-1.5" : "top-1/2 -translate-y-1/2",
          )}
          describedBy={statusId}
          dictation={dictation}
          fieldLabel={fieldLabel}
        />
        {multiline && (
          <DictationMicMenu
            className="absolute bottom-1 right-10"
            disabled={dictation.isActive}
          />
        )}
      </div>
      <DictationStatusLine dictation={dictation} id={statusId} />
    </div>
  );
};

type FieldMicParts = {
  // The mic button, to place inside the field's own layout.
  button: ReactNode;
  // The status line (live words, timer, errors), to place under the field.
  status: ReactNode;
  listening: boolean;
};

const FieldDictationMicEnabled = ({
  getTarget,
  fieldLabel,
  buttonClassName,
  children,
}: {
  getTarget: () => HTMLElement | null;
  fieldLabel: string;
  buttonClassName?: string;
  children: (parts: FieldMicParts) => ReactNode;
}): JSX.Element => {
  const statusId = useId();
  const dictation = useFieldDictation({ getTarget });
  return (
    <>
      {children({
        button: (
          <DictationButton
            className={buttonClassName}
            describedBy={statusId}
            dictation={dictation}
            fieldLabel={fieldLabel}
          />
        ),
        status: <DictationStatusLine dictation={dictation} id={statusId} />,
        listening: dictation.status === "listening",
      })}
    </>
  );
};

/**
 * The same mic as WithDictation, for a field with its own layout (a button
 * already sits inside it): `children` places the mic and the status line.
 * Without the flag, or with `enabled` false, there is no mic and the hooks
 * do not run.
 */
export const FieldDictationMic = ({
  enabled = true,
  children,
  ...props
}: {
  enabled?: boolean;
  getTarget: () => HTMLElement | null;
  fieldLabel: string;
  buttonClassName?: string;
  children: (parts: FieldMicParts | null) => ReactNode;
}): JSX.Element => {
  if (!enabled || !AppConfig.enableDictationEverywhere)
    return <>{children(null)}</>;
  return (
    <FieldDictationMicEnabled {...props}>{children}</FieldDictationMicEnabled>
  );
};

/**
 * Adds a mic to the input or textarea inside it (behind
 * VITE_DICTATION_EVERYWHERE_ENABLED). Put it around FormControl, not inside.
 */
export const WithDictation = (props: WithDictationProps): JSX.Element => {
  if (!AppConfig.enableDictationEverywhere) return <>{props.children}</>;
  return <WithDictationEnabled {...props} />;
};
